import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksElement } from '../BlocksElement';
import type { HeadingColor, HeadingLevel, HeadingVariant } from './heading.model';
import styles from './heading.styles';

/**
 * @slot default - This heading content
 * @slot before - render passed content before the header
 * @csspart base - The component's base wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart heading-wrapper - The component's heading wrapper.
 * @csspart before-wrapper - The component's before slot wrapper.
 * @csspart part-before - The component's before slot.
 */
export class BlocksHeading extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Heading variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**display-xl** renders a heading with the cat-theme-typography-preset-display-xl preset</li>
   * <li>**display-lg** renders a heading with the cat-theme-typography-preset-display-lg preset</li>
   * <li>**display** renders a heading with the cat-theme-typography-preset-display preset</li>
   * <li>**display-sm** renders a heading with the cat-theme-typography-preset-display-sm preset</li>
   * <li>**headline-lg** renders a heading with the cat-theme-typography-preset-headline-lg preset</li>
   * <li>**headline** renders a heading with the cat-theme-typography-preset-headline preset</li>
   * <li>**title** renders a heading with the cat-theme-typography-preset-title preset</li>
   * <li>**title-sm** renders a heading with the cat-theme-typography-preset-title-sm</li>
   * <li>**label-lg** renders a heading with the cat-theme-typography-preset-label-lg</li>
   * <li>**label** renders a heading with the cat-theme-typography-preset-label</li>
   * <li>**label-sm** renders a heading with the cat-theme-typography-preset-label-sm</li>
   * <li>**body** renders a heading with the cat-theme-typography-preset-body preset</li>
   * <li>**body-sm** renders a heading with the cat-theme-typography-preset-body-sm preset</li>
   * <li>**footnote** renders a heading with the cat-theme-typography-preset-footnote preset</li>
   * <li>**footnote-bold** renders a heading with the cat-theme-typography-preset-footnote-bold preset</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  variant: HeadingVariant;

  /**
   * Heading variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders a heading with cat-theme-color-content-strong</li>
   * <li>**knockout** renders a heading with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders a heading with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**error** renders a heading with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: HeadingColor;

  /**
   * Dynamic tag name for the component
   * 1) This is needed to use proper semantic heading treatments depending on where it lives on the page
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**h1** renders an `h1` tag</li>
   * <li>**h2** renders an `h2` tag. This is the default</li>
   * <li>**h3** renders an `h3` tag</li>
   * <li>**h4** renders an `h4` tag</li>
   * <li>**h5** renders an `h5` tag</li>
   * <li>**h6** renders an `h6` tag</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  level: HeadingLevel = 'h2';

  /**
   * Section decorator with brand color that displays under the heading text
   */
  @property({ type: Boolean })
  decorator: boolean | undefined;

  /**
   * if text is diabled
   */
  @property({ type: Boolean })
  isDisabled: boolean | undefined;

  protected slots = new SlotController(this, null, 'before');

  private get hasSlotBefore(): boolean {
    return this.slots.hasSlotted('before');
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-heading', {
      'cat-c-heading--display-lg': this.variant ? this.variant === 'display-lg' : this.level === 'h1',
      'cat-c-heading--display': this.variant ? this.variant === 'display' : this.level === 'h2',
      'cat-c-heading--headline-lg': this.variant ? this.variant === 'headline-lg' : this.level === 'h3',
      'cat-c-heading--headline': this.variant ? this.variant === 'headline' : this.level === 'h4',
      'cat-c-heading--title': this.variant ? this.variant === 'title' : this.level === 'h5',
      'cat-c-heading--title-sm': this.variant ? this.variant === 'title-sm' : this.level === 'h6',
      'cat-c-heading--display-xl': this.variant === 'display-xl',
      'cat-c-heading--display-sm': this.variant === 'display-sm',
      'cat-c-heading--label-lg': this.variant === 'label-lg',
      'cat-c-heading--label': this.variant === 'label',
      'cat-c-heading--label-sm': this.variant === 'label-sm',
      'cat-c-heading--body': this.variant === 'body',
      'cat-c-heading--body-sm': this.variant === 'body-sm',
      'cat-c-heading--footnote': this.variant === 'footnote',
      'cat-c-heading--footnote-bold': this.variant === 'footnote-bold',
      'cat-c-heading--knockout': this.color === 'knockout',
      'cat-c-heading--knockout-brand': this.color === 'knockout-brand',
      'cat-c-heading--error': this.color === 'error',
      'cat-c-heading--decorator': this.decorator === true,
      'cat-c-heading--disabled': this.isDisabled === true
    });

    switch (this.level) {
      case 'h1':
        return html`
          ${!this.hasSlotBefore ? html`<h1 part="base" class="${componentClassName}"><slot part="part-default"></slot></h1>` : nothing}
          <div part="heading-wrapper" class="${!this.hasSlotBefore ? 'cat-c-heading--not-has-before' : 'cat-c-heading-wrapper'}">
            <div part="before-wrapper" class="cat-c-heading__before"><slot part="part-before" name="before"></slot></div>
            <h1 part="base" class="${componentClassName}"><slot part="part-default"></slot></h1>
          </div>
        `;
      case 'h2':
        return html`
          ${!this.hasSlotBefore ? html` <h2 part="base" class="${componentClassName}"><slot part="part-default"></slot></h2>` : nothing}
          <div part="heading-wrapper" class="${!this.hasSlotBefore ? 'cat-c-heading--not-has-before' : 'cat-c-heading-wrapper'}">
            <div part="before-wrapper" class="cat-c-heading__before"><slot part="part-before" name="before"></slot></div>
            <h2 part="base" class="${componentClassName}"><slot part="part-default"></slot></h2>
          </div>
        `;
      case 'h3':
        return html`
          ${!this.hasSlotBefore ? html` <h3 part="base" class="${componentClassName}"><slot part="part-default"></slot></h3>` : nothing}
          <div part="base" class="${!this.hasSlotBefore ? 'cat-c-heading--not-has-before' : 'cat-c-heading-wrapper'}">
            <div part="before-wrapper" class="cat-c-heading__before"><slot part="part-before" name="before"></slot></div>
            <h3 part="base" class="${componentClassName}"><slot part="part-default"></slot></h3>
          </div>
        `;
      case 'h4':
        return html`
          ${!this.hasSlotBefore ? html` <h4 part="base" class="${componentClassName}"><slot part="part-default"></slot></h4> ` : nothing}
          <div part="heading-wrapper" class="${!this.hasSlotBefore ? 'cat-c-heading--not-has-before' : 'cat-c-heading-wrapper'}">
            <div part="before-wrapper" class="cat-c-heading__before"><slot part="part-before" name="before"></slot></div>
            <h4 part="base" class="${componentClassName}"><slot part="part-default"></slot></h4>
          </div>
        `;
      case 'h5':
        return html`
          ${!this.hasSlotBefore ? html` <h5 part="base" class="${componentClassName}"><slot part="part-default"></slot></h5> ` : nothing}
          <div part="heading-wrapper" class="${!this.hasSlotBefore ? 'cat-c-heading--not-has-before' : 'cat-c-heading-wrapper'}">
            <div part="before-wrapper" class="cat-c-heading__before"><slot part="part-before" name="before"></slot></div>
            <h5 part="base" class="${componentClassName}"><slot part="part-default"></slot></h5>
          </div>
        `;
      case 'h6':
        return html`
          ${!this.hasSlotBefore ? html` <h6 part="base" class="${componentClassName}"><slot part="part-default"></slot></h6>` : nothing}
          <div part="heading-wrapper" class="${!this.hasSlotBefore ? 'cat-c-heading--not-has-before' : 'cat-c-heading-wrapper'}">
            <div part="before-wrapper" class="cat-c-heading__before"><slot part="part-before" name="before"></slot></div>
            <h6 part="base" class="${componentClassName}"><slot part="part-default"></slot></h6>
          </div>
        `;
    }
  }
}

if (customElements.get('cat-heading') === undefined) {
  customElements.define('cat-heading', BlocksHeading);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-heading': BlocksHeading;
  }
}
